import { useEffect } from 'react';
import { formatDecisionCitation, formatDecisionTitle } from '@hector/decision-document';
import { useDecision } from '@/hooks/useDecision';
import { useRouteCaseId } from '@/hooks/useRouteCaseId';
import { DecisionViewerService } from '@/services/decisionViewerService';
import type { DecisionPreviewTab } from '@/stores/previewPanelStore';
import { usePreviewPanelStore } from '@/stores/previewPanelStore';
import { DecisionView } from './DecisionView';
import { DownloadDecisionButton } from './DownloadDecisionButton';
import { SaveDecisionButton } from './SaveDecisionButton';

/**
 * Rangée de chrome d'un onglet jurisprudence.
 *
 * Ni bascule de vue ni OCR : le `full_text` du corpus EST le texte. Les deux
 * actions propres à un arrêt sont « Enregistrer » (le matérialiser en pièce d'un
 * dossier) et l'export PDF — deux mécaniques sans équivalent côté pièce, d'où
 * une rangée entièrement distincte plutôt que des conditions dans la chrome.
 */
export function DecisionPreviewTabActions({ tab }: { tab: DecisionPreviewTab }) {
  const { data } = useDecision(tab.ordre, tab.decisionId);
  // Le dossier vient de l'ouvreur quand il le connaît (une citation du chat en
  // porte un), sinon de l'URL. Sans ce repli, « Enregistrer » s'ouvrait sans
  // dossier présélectionné dès qu'on avait ouvert l'arrêt depuis les résultats
  // de recherche, qui n'en fournissent pas.
  const routeCaseId = useRouteCaseId();
  const preselectedCaseId = tab.caseId ?? routeCaseId;
  if (!data) return null;

  return (
    <>
      <SaveDecisionButton decision={data} caseId={preselectedCaseId} />
      <DownloadDecisionButton decision={data} />
    </>
  );
}

/**
 * Corps d'un onglet jurisprudence.
 *
 * Le texte de l'arrêt NE PASSE PAS par le store : il vit dans le cache TanStack
 * de `useDecision` (`staleTime: Infinity`, corpus immuable), partagé avec la page
 * `/decisions/:ordre/:id`. Le store ne porte que l'identité et le libellé — c'est
 * la frontière posée par `CLAUDE.md` : coque partagée, données séparées.
 *
 * ⚠️ `data-testid="decision-preview-panel"` est l'ancrage des tests e2e du viewer
 * JP (`tests/full/12-admin-jp-viewer.spec.ts`). Il a suivi le composant quand le
 * panneau autonome est devenu un onglet — ne pas le « nettoyer ».
 */
export function DecisionPreviewTabBody({ tab }: { tab: DecisionPreviewTab }) {
  const { data, isLoading, isError } = useDecision(tab.ordre, tab.decisionId);
  const renameTab = usePreviewPanelStore((state) => state.renameTab);
  // Même repli que dans la chrome : suivre la chaîne ne doit pas perdre le dossier.
  const routeCaseId = useRouteCaseId();
  const preselectedCaseId = tab.caseId ?? routeCaseId;

  useEffect(() => {
    // Un onglet JP naît sans nom — le titre d'un arrêt ne se connaît qu'après le
    // fetch. Projection d'AFFICHAGE à sens unique : rien ne relit ce libellé pour
    // décider quoi que ce soit, et `renameTab` est idempotent.
    if (data) renameTab(tab.key, formatDecisionTitle(data), formatDecisionCitation(data));
  }, [data, renameTab, tab.key]);

  return (
    // `pt-0` : l'espace au-dessus appartient à la rangée d'actions, qui le porte
    // pour tout le panneau. En rajouter ici décentrerait ses boutons vers le haut.
    <div data-testid="decision-preview-panel" className="h-full w-full overflow-auto px-5 pb-4 pt-0">
      {isLoading && <p className="text-sm italic text-grey-500">Chargement…</p>}
      {isError && <p className="text-sm text-grey-600">Décision introuvable.</p>}
      {data && (
        <DecisionView
          decision={data}
          onOpenCorpusDecision={(corpusId) =>
            // Nouvel onglet, pas un remplacement : l'arrêt d'où l'on part reste
            // ouvert, ce qui rend le retour arrière que le swap in-place ne
            // permettait pas. `ordre` est hérité de l'onglet source — la chaîne
            // d'une décision judiciaire est judiciaire — au lieu d'être supposé.
            DecisionViewerService.viewDecision({
              ordre: tab.ordre,
              id: corpusId,
              caseId: preselectedCaseId,
            })
          }
        />
      )}
    </div>
  );
}
